import {Injectable}                     from '@angular/core';
import {ApDynformsConfigFieldset}       from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynformsConfigNumerictextbox} from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {ApDynformsValidator}            from '../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                     from '@angular/forms';
import {AgriportConstantsEnum}          from '../../../ap-interface/enums/ap-agriport-constants.enum';
import {AgriportConstantsService}       from '../../../services/common/agriport-constants.service';
import {ApDynGridColumnConfigBase}      from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                       from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {GetRoundNumericPipe}            from '../../../ap-utils';
import {Observable}                     from 'rxjs';
import {ApDynformsConfigPlaceholder}    from '../../../ap-dynforms/config/ap-dynforms-config-placeholder';
import {map}                            from 'rxjs/operators';
import {SettingsStore}                  from '../../../stores/base-data/settings.store';
import {ApNutrientService}              from '../../../ap-utils/service/ap-nutrient.service';
import {ApElementType}                  from '../../../ap-interface/enums/ap-elements-type.enum';
import {FertilizerStore}                from '../../../stores/base-data/fertilizer.store';
import {ApTranslationService}           from '../../../ap-utils/service/ap-translation.service';

@Injectable()
export class UniformConfig {
  constructor(private settingsStore: SettingsStore,
              private fertilizerStore: FertilizerStore,
              private nutrientService: ApNutrientService,
              private translationService: ApTranslationService,
              private agriportConstantsService: AgriportConstantsService,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  /**
   * Builds the form configuration for the uniform application mode
   * Creates a fieldset with EC stage, constant amount, and product amount fields
   * @param formValues Current form values
   * @param formValues$ Observable of form values
   * @param isModalField Indicates if the form is for a modal dialog
   * @returns Configured fieldset for uniform application
   * @public
   */
  public formBuilder(formValues: any, formValues$: Observable<any>, isModalField: boolean): ApDynformsConfigFieldset {
    return new ApDynformsConfigFieldset({
      legend: 'Nutrients_Pages_Popups__Amount',
      key: 'Amount',
      useMaxWidth: isModalField,
      config: [
        new ApDynformsConfigNumerictextbox({
          key: 'ECStage',
          label: 'Global__ECStage',
          value: formValues['ECStage'],
          decimals: 0,
          format: 'n0',
          step: 1,
          min: 0,
          max: +this.agriportConstantsService.GetConstant(AgriportConstantsEnum.EcMax),
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
          ]
        }),
        new ApDynformsConfigPlaceholder(),
        new ApDynformsConfigNumerictextbox({
          key: 'Constant',
          label: 'Nutrients__Constant_Amount_kg_N_per_ha',
          infoText: 'Info_Uniform_NF',
          value: formValues['Constant'],
          decimals: 0,
          format: 'n0',
          step: 1,
          min: 1,
          max: 250,
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
          ]
        }),
        new ApDynformsConfigNumerictextbox({
          key: 'ProductAmount',
          label: 'Global__Product',
          label$: formValues$.pipe(map(values => this.productAmountLable(this.getProductId(isModalField, formValues, values)))),
          value: this.productAmountCalculation(formValues['Constant'], formValues['Product']),
          value$: formValues$.pipe(
            map(values => this.productAmountCalculation(values['Constant'], this.getProductId(isModalField, formValues, values)))
          ),
          format: 'n0',
          step: 1,
          decimals: this.settingsStore.FirstSetting.DigitsAfterDecimalPoint,
          disabled: true,
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
          ]
        })
      ]
    });
  }

  /**
   * Generates grid column configuration for the uniform application mode
   * Creates a column for the constant rate with specific formatting
   * @returns Array of grid column configurations
   * @public
   */
  public gridBuilderConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__RateConst',
        field: 'Constant',
        columnLoading: 'ColumnLoading',
        width: 90,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [0]
        }]
      }),
    ];
  }

  /**
   * Generates a product amount label based on the selected product's unit
   * Combines the main product label with its unit of measurement
   * @param productId ID of the selected product
   * @returns Formatted product label with unit
   * @private
   */
  private productAmountLable(productId: number | undefined): string {
    const fertilizer = this.fertilizerStore.getFertilizer(productId);
    const unitKey = fertilizer?.Unit?.Key ?? fertilizer?.Unit?.BaseUnit?.Key;
    if (!unitKey) {
      return 'Global__Product';
    }
    const perHa = this.translationService.translate('Global__Unit_ha');
    const mainTitle = this.translationService.translate('Global__Product');
    return `${mainTitle} [${this.translationService.translate(unitKey)}/${perHa}]`;
  }

  /**
   * Determines the appropriate product ID source based on the field context
   * Returns product ID from either form values or listener form values
   * @param isModalField Boolean indicating if the field is in a modal dialog
   * @param formValues Original form values object
   * @param listenerFormValues Form values from the listener/observable
   * @returns Product ID from the appropriate source
   * @private
   */
  private getProductId(isModalField: boolean, formValues: any, listenerFormValues: any): number | undefined {
    return isModalField ? formValues['Product'] : listenerFormValues['Product'];
  }

  /**
   * Calculates the product amount based on the constant value and selected product
   * Converts the nitrogen (N) nutrient amount to product quantity
   * @param constantValue Constant application rate
   * @param productId ID of the selected product
   * @returns Calculated product amount
   * @private
   */
  private productAmountCalculation(constantValue: number | undefined,
                                   productId: number | undefined): number {
    if (!Number.isFinite(constantValue) || !Number.isFinite(productId)) {
      return 0;
    }
    return this.nutrientService.convertNutrientToGoods(productId, ApElementType.N, constantValue);
  }
}
